// ============================================
// FILE: src/components/stripe/SubscriptionPaymentModal.js
// VERSION CORRIGÉE - Navigation + Gestion d'erreur
// ============================================

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom' // ✅ AJOUT IMPORT
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CAlert,
  CButton,
  CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilLockLocked, cilCheckCircle, cilWarning, cilReload } from '@coreui/icons'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import api from '../../utils/apiClient'
import { getStripePromise } from '../../services/stripeService'

// ============================================
// Composant formulaire de paiement
// ============================================
const CheckoutForm = ({ plan, onSuccess, onError }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!stripe || !elements) {
      console.error('❌ Stripe ou Elements non chargés')
      return
    }

    setLoading(true)
    console.log('🔄 Début du traitement du paiement...')

    try {
      // Étape 1 : Confirmer le Setup Intent
      console.log('1️⃣ Confirmation du Setup Intent...')
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/subscription/success`,
        },
      })

      if (stripeError) {
        console.error('❌ Erreur Stripe:', stripeError)
        onError(stripeError.message)
        setLoading(false)
        return
      }

      console.log('✅ Setup Intent confirmé:', setupIntent.id)

      // Étape 2 : Créer l'abonnement côté backend
      console.log("2️⃣ Création de l'abonnement backend...")
      const response = await api.post('/stripe/subscriptions/create-with-payment', {
        plan: plan,
        paymentMethodId: setupIntent.payment_method,
      })

      console.log('✅ Réponse backend:', response)

      // Étape 3 : Si le paiement nécessite une action supplémentaire (3DS)
      if (response.requiresAction && response.clientSecret) {
        console.log('3️⃣ Action supplémentaire requise (3DS)...')
        const { error: confirmError } = await stripe.confirmCardPayment(response.clientSecret)

        if (confirmError) {
          console.error('❌ Erreur confirmation 3DS:', confirmError)
          onError(confirmError.message)
          setLoading(false)
          return
        }
      }

      // ✅ Succès !
      console.log('✅ Paiement réussi, appel onSuccess()')
      onSuccess()
    } catch (err) {
      console.error('❌ Erreur globale:', err)
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.details ||
        err.message ||
        'Erreur lors du traitement du paiement'

      onError(errorMessage)
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="payment-element-container">
        <PaymentElement />
      </div>
      <CButton
        type="submit"
        color="primary"
        size="lg"
        className="w-100 payment-submit-button"
        disabled={!stripe || !elements || loading}
      >
        {loading ? (
          <>
            <CSpinner size="sm" className="me-2" />
            Traitement en cours...
          </>
        ) : (
          <>
            <CIcon icon={cilLockLocked} className="me-2" />
            Confirmer le paiement
          </>
        )}
      </CButton>
    </form>
  )
}

// ============================================
// Modal principale
// ============================================
const SubscriptionPaymentModal = ({ visible, onClose, plan, onSuccess }) => {
  const navigate = useNavigate() // ✅ AJOUT DU HOOK
  const [error, setError] = useState(null)
  const [clientSecret, setClientSecret] = useState(null)
  const [loadingSetup, setLoadingSetup] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false) // ✅ AJOUT
  const [stripePromise, setStripePromise] = useState(null)

  // Charger la publishable key dynamiquement depuis le backend
  useEffect(() => {
    if (visible) {
      getStripePromise().then((promise) => {
        if (promise) {
          setStripePromise(promise)
        } else {
          setError('Impossible de charger la configuration de paiement')
        }
      })
    }
  }, [visible])

  useEffect(() => {
    if (visible && plan) {
      setPaymentSuccess(false) // Reset à chaque ouverture
      createSetupIntent()
    }
  }, [visible, plan])

  const createSetupIntent = async () => {
    setLoadingSetup(true)
    setError(null)

    try {
      console.log('🔧 Création du Setup Intent...')

      const data = await api.post('/stripe/subscriptions/create-setup-intent', {})

      console.log('✅ Setup Intent créé:', data.clientSecret.substring(0, 20) + '...')
      setClientSecret(data.clientSecret)
    } catch (err) {
      console.error('❌ Erreur création Setup Intent:', err)
      setError(err.response?.data?.error || "Erreur lors de l'initialisation du paiement")
    } finally {
      setLoadingSetup(false)
    }
  }

  const handleSuccess = () => {
    console.log('🎉 handleSuccess appelé')

    // Vider le cache d'erreur
    sessionStorage.removeItem('subscriptionError')

    // Marquer le succès
    setPaymentSuccess(true)

    // Émettre l'événement de mise à jour
    window.dispatchEvent(new CustomEvent('subscription_updated'))

    // Appeler le callback parent si fourni
    if (onSuccess) {
      onSuccess()
    }

    // Redirection après 2 secondes
    setTimeout(() => {
      console.log('🔄 Redirection vers /subscription/success')
      navigate('/subscription/success') // ✅ MAINTENANT DÉFINI
    }, 2000)
  }

  const handleError = (errorMessage) => {
    console.error('❌ Erreur de paiement:', errorMessage)
    setError(errorMessage)
  }

  const handleClose = () => {
    if (!paymentSuccess) {
      // Empêcher la fermeture si succès en cours
      setError(null)
      setClientSecret(null)
      setPaymentSuccess(false)
      onClose()
    }
  }

  if (!stripePromise) {
    return (
      <CModal visible={visible} onClose={onClose} size="lg" alignment="center">
        <CModalHeader closeButton>
          <CModalTitle>{error ? 'Erreur de configuration' : 'Chargement...'}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {error ? (
            <CAlert color="danger">{error}</CAlert>
          ) : (
            <div className="text-center p-4">
              <CSpinner color="primary" />
              <p className="mt-3">Chargement de la configuration de paiement...</p>
            </div>
          )}
        </CModalBody>
      </CModal>
    )
  }

  return (
    <>
      <style>{`
        .payment-modal-header {
          border-bottom: 1px solid #e5e7eb;
          padding: 24px 32px;
        }
        
        .payment-modal-body {
          padding: 32px;
        }
        
        .plan-summary {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 32px;
        }
        
        .plan-summary-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        
        .plan-name {
          font-size: 20px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }
        
        .plan-description {
          font-size: 14px;
          color: #6b7280;
        }
        
        .plan-price-display {
          text-align: right;
        }
        
        .price-amount {
          font-size: 32px;
          font-weight: 700;
          color: #1f2937;
          line-height: 1;
        }
        
        .price-interval {
          font-size: 14px;
          color: #6b7280;
        }
        
        .plan-details-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-top: 1px solid #e5e7eb;
          font-size: 14px;
        }
        
        .detail-label {
          color: #6b7280;
        }
        
        .detail-value {
          font-weight: 600;
          color: #1f2937;
        }
        
        .payment-element-container {
          margin-bottom: 24px;
        }
        
        .payment-submit-button {
          border-radius: 8px;
          padding: 14px 24px;
          font-weight: 600;
          font-size: 16px;
        }
        
        .security-notice {
          background: #f0fdf4;
          border: 1px solid #86efac;
          border-radius: 8px;
          padding: 16px;
          margin-top: 24px;
          display: flex;
          align-items: start;
        }
        
        .security-icon {
          color: #16a34a;
          margin-right: 12px;
          flex-shrink: 0;
          margin-top: 2px;
        }
        
        .security-text {
          font-size: 13px;
          color: #166534;
          line-height: 1.5;
        }
        
        .loading-state {
          text-align: center;
          padding: 48px 0;
        }
        
        .loading-spinner {
          margin-bottom: 16px;
        }
        
        .loading-text {
          font-size: 14px;
          color: #6b7280;
        }
        
        .success-state {
          text-align: center;
          padding: 48px 0;
        }
      `}</style>

      <CModal
        visible={visible}
        onClose={handleClose}
        size="lg"
        alignment="center"
        backdrop="static"
      >
        <CModalHeader closeButton={!paymentSuccess} className="payment-modal-header">
          <CModalTitle>
            <CIcon icon={cilLockLocked} className="me-2" />
            Paiement sécurisé
          </CModalTitle>
        </CModalHeader>

        <CModalBody className="payment-modal-body">
          {/* ✅ Affichage du succès */}
          {paymentSuccess && (
            <div className="success-state">
              <CIcon icon={cilCheckCircle} size="4xl" className="text-success mb-3" />
              <h4 className="text-success mb-2">Paiement réussi !</h4>
              <p className="text-muted">Redirection en cours...</p>
              <CSpinner color="success" />
            </div>
          )}

          {/* Messages d'erreur */}
          {!paymentSuccess && error && (
            <CAlert color="danger" dismissible onClose={() => setError(null)} className="mb-4">
              <CIcon icon={cilWarning} className="me-2" />
              {error}
            </CAlert>
          )}

          {!paymentSuccess && plan && (
            <>
              {/* Résumé du plan */}
              <div className="plan-summary">
                <div className="plan-summary-header">
                  <div>
                    <div className="plan-name">{plan.name}</div>
                    {plan.description && <div className="plan-description">{plan.description}</div>}
                  </div>
                  <div className="plan-price-display">
                    <div className="price-amount">{plan.price}€</div>
                    <div className="price-interval">par {plan.interval}</div>
                  </div>
                </div>

                <div className="plan-details-row">
                  <span className="detail-label">Facturation</span>
                  <span className="detail-value">Mensuelle</span>
                </div>

                <div className="plan-details-row">
                  <span className="detail-label">Prochain paiement</span>
                  <span className="detail-value">
                    {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>

              {/* Formulaire de paiement */}
              {loadingSetup ? (
                <div className="loading-state">
                  <CSpinner color="primary" className="loading-spinner" />
                  <div className="loading-text">Initialisation du paiement sécurisé...</div>
                </div>
              ) : clientSecret ? (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#3b82f6',
                        colorBackground: '#ffffff',
                        colorText: '#1f2937',
                        colorDanger: '#ef4444',
                        fontFamily: 'system-ui, sans-serif',
                        spacingUnit: '4px',
                        borderRadius: '8px',
                      },
                    },
                  }}
                >
                  <CheckoutForm plan={plan.planCode || plan.id} onSuccess={handleSuccess} onError={handleError} />
                </Elements>
              ) : (
                <div className="text-center">
                  <CAlert color="warning" className="mb-3">
                    Impossible d'initialiser le paiement
                  </CAlert>
                  <CButton color="primary" onClick={createSetupIntent}>
                    <CIcon icon={cilReload} className="me-2" />
                    Réessayer
                  </CButton>
                </div>
              )}

              {/* Notice de sécurité */}
              <div className="security-notice">
                <CIcon icon={cilCheckCircle} size="lg" className="security-icon" />
                <div className="security-text">
                  <strong>Paiement 100% sécurisé</strong>
                  <br />
                  Vos informations bancaires sont cryptées et ne sont jamais stockées sur nos
                  serveurs. Tous les paiements sont traités en toute sécurité par Stripe.
                </div>
              </div>
            </>
          )}
        </CModalBody>
      </CModal>
    </>
  )
}

export default SubscriptionPaymentModal
